import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import BreedCard from "@/components/BreedCard";
import { HERO_IMAGE, ABOUT_IMAGE, FEATURE_IMAGE } from "@/data/breeds";
import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { useLocalizedBreeds, useLocalizedArticles, useLocalizedLearningTopics } from "@/i18n/hooks";

export default function Home() {
    const [scrollY, setScrollY] = useState(0);
    const { t } = useLang();
    const breeds = useLocalizedBreeds();
    const articles = useLocalizedArticles();
    const learningTopics = useLocalizedLearningTopics();
    const whyItems = t("home.why.items");

    useEffect(() => {
        const onScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const featuredArticle = articles.find((a) => a.featured) || articles[3];

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative h-[92vh] min-h-[560px] md:min-h-[720px] w-full overflow-hidden" data-testid="hero-section">
                <div
                    className="absolute inset-0 will-change-transform"
                    style={{ transform: `translate3d(0, ${scrollY * 0.35}px, 0) scale(1.08)` }}
                >
                    <img
                        src={HERO_IMAGE}
                        alt="Dairy cows grazing at sunrise in Karnataka"
                        className="w-full h-full object-cover object-[45%_55%] md:object-center"
                    />
                </div>
                <div className="absolute inset-0 hero-scrim" />
                <div className="absolute inset-0 grain" />

                <div className="relative h-full container-editorial flex flex-col justify-end pb-24 md:pb-32">
                    <div className="max-w-3xl text-white">
                        <div className="overlabel text-gold-light mb-6 animate-fade-in">{t("home.heroOverline")}</div>
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] mb-8 animate-fade-up whitespace-pre-line">
                            {t("home.heroBrand")}
                        </h1>
                        <div className="max-w-xl animate-fade-up" style={{ animationDelay: "220ms" }}>
                            <p className="text-2xl md:text-3xl font-serif italic text-white/95 mb-6 leading-snug">
                                {t("home.heroTagline")}
                            </p>
                            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-lg">
                                {t("home.heroSub")}
                            </p>
                        </div>
                        <div className="mt-12 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "420ms" }}>
                            <Link to="/quiz" data-testid="hero-cta-quiz" className="group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-paper transition-colors duration-500">
                                <span className="w-2 h-2 rounded-full bg-forest animate-pulse" />
                                {t("common.buttons.takeQuiz")}
                                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                            <Link to="/breeds" data-testid="hero-cta-breeds" className="group inline-flex items-center gap-3 bg-paper/95 text-ink px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-paper transition-colors duration-500">
                                {t("common.buttons.exploreBreeds")}
                                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                            <Link to="/knowledge" data-testid="hero-cta-learn" className="group inline-flex items-center gap-3 border border-white/40 text-white px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:border-white hover:bg-white/10 transition-all duration-500">
                                {t("common.buttons.learnDairy")}
                                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-6 right-6 md:right-12 text-white/60 text-[10px] tracking-[0.3em] uppercase flex items-center gap-3">
                    <span className="h-px w-10 bg-white/50" /> {t("common.labels.scroll")}
                </div>
            </section>

            {/* ABOUT */}
            <section className="py-24 md:py-40 container-editorial" data-testid="about-section">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                    <Reveal className="md:col-span-6">
                        <div className="relative overflow-hidden rounded-[12px] aspect-[4/5] shadow-editorial bg-earth/10">
                            <img src={ABOUT_IMAGE} alt="Mother cow and calf, warm morning light" className="w-full h-full object-cover object-[center_45%] hover:scale-105 transition-transform duration-[1600ms] ease-editorial" />
                        </div>
                    </Reveal>
                    <Reveal delay={150} className="md:col-span-6 md:pl-8">
                        <SectionLabel number="01" label={t("home.about.label")} className="mb-8" />
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-10 whitespace-pre-line">
                            {t("home.about.heading")}
                        </h2>
                        <div className="space-y-6 text-lg leading-relaxed text-ink/80 max-w-xl">
                            <p>{t("home.about.p1")}</p>
                            <p>{t("home.about.p2")}</p>
                        </div>
                        <div className="mt-12 flex items-center gap-8 pt-8 border-t border-border">
                            <div>
                                <div className="font-serif text-4xl text-forest kbd-num">06</div>
                                <div className="overlabel text-earth mt-1">{t("home.about.stat1")}</div>
                            </div>
                            <div className="w-px h-12 bg-border" />
                            <div>
                                <div className="font-serif text-4xl text-forest kbd-num">11</div>
                                <div className="overlabel text-earth mt-1">{t("home.about.stat2")}</div>
                            </div>
                            <div className="w-px h-12 bg-border" />
                            <div>
                                <div className="font-serif text-4xl text-forest kbd-num">00</div>
                                <div className="overlabel text-earth mt-1">{t("home.about.stat3")}</div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BREEDS */}
            <section className="py-24 md:py-32 bg-white" data-testid="breeds-section">
                <div className="container-editorial">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
                        <div>
                            <SectionLabel number="02" label={t("home.breedsSection.label")} className="mb-6" />
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl whitespace-pre-line">
                                {t("home.breedsSection.heading")}
                            </h2>
                        </div>
                        <Link to="/breeds" className="link-underline text-sm text-forest inline-flex items-center gap-2 self-start md:self-end" data-testid="home-view-all-breeds">
                            {t("common.buttons.viewAll")} <ArrowUpRight size={16} strokeWidth={1.5} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {breeds.map((b, i) => (
                            <Reveal key={b.slug} delay={i * 90}>
                                <BreedCard breed={b} index={i} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section className="py-24 md:py-40 container-editorial" data-testid="why-section">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                    <Reveal className="md:col-span-5">
                        <SectionLabel number="03" label={t("home.why.label")} className="mb-8" />
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight whitespace-pre-line">
                            {t("home.why.heading")}
                        </h2>
                        <p className="mt-8 text-ink/70 max-w-md leading-relaxed">{t("home.why.body")}</p>
                    </Reveal>
                    <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                        {(Array.isArray(whyItems) ? whyItems : []).map((t2, i) => (
                            <Reveal key={t2} delay={i * 60}>
                                <div className="border-b border-border py-5 flex items-baseline gap-4">
                                    <span className="font-serif text-gold text-sm kbd-num">0{i + 1}</span>
                                    <span className="font-serif text-2xl text-ink">{t2}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEARN */}
            <section className="py-24 md:py-32 bg-forest text-paper relative overflow-hidden" data-testid="learn-section">
                <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                <div className="container-editorial relative">
                    <div className="max-w-3xl mb-16">
                        <SectionLabel number="04" label={t("home.learn.label")} className="mb-8 text-paper" />
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-paper whitespace-pre-line">
                            {t("home.learn.heading")}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-2">
                        {learningTopics.map((topic, i) => (
                            <Reveal key={topic.title} delay={i * 45}>
                                <div className="border-t border-paper/15 py-8 group">
                                    <div className="flex items-baseline gap-4 mb-3">
                                        <span className="font-serif text-gold-light text-sm kbd-num">{String(i + 1).padStart(2, "0")}</span>
                                        <h3 className="font-serif text-2xl md:text-3xl leading-tight text-paper">{topic.title}</h3>
                                    </div>
                                    <p className="text-paper/60 leading-relaxed pl-8">{topic.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* QUIZ CTA */}
            <section className="py-24 md:py-32 container-editorial" data-testid="quiz-cta-section">
                <Reveal>
                    <div className="relative overflow-hidden rounded-[16px] bg-ink text-paper shadow-editorial">
                        <div className="absolute inset-0 opacity-45">
                            <img src="/generated/hero.jpg" alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
                        </div>
                        <div className="absolute inset-0 grain opacity-40" />
                        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center p-10 md:p-16 lg:p-20">
                            <div className="md:col-span-8">
                                <div className="overlabel text-gold-light mb-6 flex items-center gap-3">
                                    <span className="h-px w-8 bg-gold-light" /> {t("home.quizCta.label")}
                                </div>
                                <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-6">
                                    {t("home.quizCta.heading1")}<br />{t("home.quizCta.heading2")} <em className="not-italic text-gold">{t("home.quizCta.heading3")}</em>
                                </h2>
                                <p className="text-paper/75 text-lg max-w-lg leading-relaxed mb-10">{t("home.quizCta.body")}</p>
                                <Link to="/quiz" data-testid="home-quiz-cta" className="group inline-flex items-center gap-3 bg-gold text-ink px-10 py-5 rounded-full text-sm tracking-wide font-medium hover:bg-paper transition-colors duration-500">
                                    {t("common.buttons.takeQuizShort")}
                                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            </div>
                            <div className="md:col-span-4 hidden md:block">
                                <div className="text-right">
                                    <div className="font-serif text-8xl text-gold kbd-num leading-none">05</div>
                                    <div className="overlabel text-gold-light mt-4">{t("home.quizCta.questionsLabel")}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* FEATURED */}
            <section className="py-24 md:py-40 container-editorial" data-testid="featured-article-section">
                <Reveal>
                    <SectionLabel number="05" label={t("home.featured.label")} className="mb-8" />
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                    <Reveal delay={100} className="md:col-span-7 order-2 md:order-1">
                        <div className="max-w-xl">
                            <div className="overlabel text-earth mb-6">{featuredArticle.category} · {featuredArticle.readTime}</div>
                            <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
                                {featuredArticle.title}
                            </h3>
                            <p className="text-lg leading-relaxed text-ink/75 mb-10">{featuredArticle.excerpt}</p>
                            <Link to="/knowledge" data-testid="featured-article-cta" className="inline-flex items-center gap-3 text-forest border-b border-forest pb-1 text-sm font-medium hover:gap-4 transition-[gap] duration-500">
                                {t("common.buttons.readStory")} <ArrowRight size={16} strokeWidth={1.5} />
                            </Link>
                        </div>
                    </Reveal>
                    <Reveal delay={0} className="md:col-span-5 order-1 md:order-2">
                        <div className="relative aspect-[4/5] overflow-hidden rounded-[12px] shadow-editorial">
                            <img src={FEATURE_IMAGE} alt={featuredArticle.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1600ms] ease-editorial" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="py-32 md:py-48 bg-paper" data-testid="cta-section">
                <div className="container-editorial text-center max-w-4xl mx-auto">
                    <Reveal>
                        <SectionLabel number="06" label={t("home.begin.label")} className="mb-8 justify-center [&]:justify-center" />
                    </Reveal>
                    <Reveal delay={100}>
                        <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-12">
                            {t("home.begin.heading1")}<br /><em className="not-italic text-earth">{t("home.begin.heading2")}</em> {t("home.begin.heading3")}
                        </h2>
                    </Reveal>
                    <Reveal delay={200}>
                        <p className="text-lg text-ink/70 max-w-xl mx-auto mb-14 leading-relaxed">{t("home.begin.body")}</p>
                        <Link to="/breeds" data-testid="cta-explore-breeds" className="group inline-flex items-center gap-3 bg-forest text-paper px-10 py-5 rounded-full text-sm tracking-wide font-medium hover:bg-gold hover:text-ink transition-colors duration-500">
                            {t("common.buttons.exploreAll")}
                            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
